/**
 * Purpose: Plugin registry for Owner Dashboard modules — the management-side
 *          mirror of businessModules.js (D-12). Adding a dashboard capability =
 *          append ONE descriptor + component file. The dashboard shell, its nav,
 *          and its renderer never change.
 * Inputs: dashboard context ({ business, data }).
 * Outputs: DASHBOARD_MODULE_REGISTRY (descriptors),
 *          resolveDashboardModules(ctx) → renderable descriptors,
 *          getDefaultDashboardModuleId(modules).
 *
 * Descriptor contract:
 *   {
 *     id: 'products',                      // stable id; also the URL segment
 *     title: (ctx) => string,              // label — ALWAYS config, never hardcoded
 *     icon: node,                          // nav icon
 *     requiresModule?: 'catalogue',        // only show if the business enabled
 *                                          // that detail-page module (optional)
 *     isAvailable: (ctx) => boolean,       // cheap, side-effect free
 *     Component: React.lazy(() => import(…)),
 *   }
 *
 * Enablement reuses `getEnabledModuleIds` from businessModules — ONE source of
 * truth for what a business has switched on (enabled_modules JSON ?? category
 * defaults). A dashboard module never invents its own flag namespace.
 *
 * Owner-only by construction: the dashboard route itself is gated on
 * canManageOwnedResource and every mutation hits an endpoint the server
 * re-authorizes. `isAvailable` is a UX filter, never a security boundary.
 */
import { lazy } from 'react';
import { getEnabledModuleIds, getModuleLabel } from './businessModules';

const OverviewModule = lazy(() =>
  import('../components/businesses/dashboard/modules/OverviewModule'),
);
const ProductsModule = lazy(() =>
  import('../components/businesses/dashboard/modules/ProductsModule'),
);
const ServicesModule = lazy(() =>
  import('../components/businesses/dashboard/modules/ServicesModule'),
);
const SettingsModule = lazy(() =>
  import('../components/businesses/dashboard/modules/SettingsModule'),
);

const icon = (d) => (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={d} />
  </svg>
);

const GRID_D =
  'M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z';
const BOX_D = 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4';
const WRENCH_D =
  'M11 4a2 2 0 114 0 2 2 0 01-4 0zM7 20a2 2 0 100-4 2 2 0 000 4zM17 20a2 2 0 100-4 2 2 0 000 4zM11 6v10M15 6v4a2 2 0 01-2 2H9a2 2 0 00-2 2v2';
const COG_D =
  'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065zM15 12a3 3 0 11-6 0 3 3 0 016 0z';

export const DASHBOARD_MODULE_REGISTRY = [
  {
    id: 'overview',
    title: () => 'Overview',
    icon: icon(GRID_D),
    isAvailable: () => true,
    Component: OverviewModule,
  },
  {
    // Label is category-configured: a restaurant manages a "Menu", a shop
    // "Products" (getModuleLabel, shared with the detail page — D-03).
    id: 'products',
    title: (ctx) => getModuleLabel(ctx.business, 'catalogue', 'Products'),
    icon: icon(BOX_D),
    requiresModule: 'catalogue',
    isAvailable: () => true,
    Component: ProductsModule,
  },
  {
    id: 'services',
    title: (ctx) => getModuleLabel(ctx.business, 'services', 'Services'),
    icon: icon(WRENCH_D),
    requiresModule: 'services',
    isAvailable: () => true,
    Component: ServicesModule,
  },
  {
    // Settings is always available (every owner can edit their profile). Its
    // sub-sections live in SETTINGS_REGISTRY, not here — adding one never touches
    // this registry or the shell.
    id: 'settings',
    title: () => 'Settings',
    icon: icon(COG_D),
    isAvailable: () => true,
    Component: SettingsModule,
  },
];

/**
 * Ordered dashboard modules for this business.
 * enabled flags (shared with the detail page) ⊕ availability.
 * A descriptor without `requiresModule` is always eligible — Overview and the
 * future settings modules are not tied to a customer-facing capability.
 */
export const resolveDashboardModules = (ctx) => {
  if (!ctx?.business) return [];
  const enabled = getEnabledModuleIds(ctx.business);
  return DASHBOARD_MODULE_REGISTRY.filter(
    (m) => (!m.requiresModule || enabled.includes(m.requiresModule)) && m.isAvailable(ctx),
  );
};

/** First resolved module — the landing panel when the URL names none. */
export const getDefaultDashboardModuleId = (modules) => modules[0]?.id || null;

/** Labels stay configuration: a restaurant manages a "Menu", a shop "Products". */
export const getDashboardModuleLabel = getModuleLabel;
